import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Pencil, Plus, Trash2, Search } from "lucide-react";
import { toast } from "sonner";
import SelectWithAdd from "@/components/SelectWithAdd";
import BulkUploadButton from "@/components/BulkUploadButton";
import { Th, Td } from "@/pages/CustomersPage";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY = { device_model_id: "", device_imei_number: "", status: "Active" };

export default function DeviceDetailsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [models, setModels] = useState([]);
  const [availSims, setAvailSims] = useState([]);
  const [filter, setFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/device-details").then((r) => setItems(r.data));
  const loadModels = () => api.get("/device-models").then((r) => setModels(r.data));
  const loadAvailSims = (excludeDeviceId) => {
    const q = excludeDeviceId ? `?exclude_device_id=${excludeDeviceId}` : "";
    return api.get(`/sims/available${q}`).then((r) => setAvailSims(r.data));
  };

  // Initial data load — mount-only by design.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    load();
    loadModels();
    loadAvailSims();
  }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); loadAvailSims(); setOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      device_model_id: row.device_model_id || "",
      device_imei_number: row.device_imei_number || "",
      status: row.status || "Active",
      sim_id: row.sim_id || "",
    });
    loadAvailSims(row.id);
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.device_model_id) {
      toast.error("Device Model is required");
      return;
    }
    if (!form.device_imei_number.trim()) {
      toast.error("Device IMEI Number is required");
      return;
    }
    setSaving(true);
    const payload = { ...form, sim_id: form.sim_id === "__none__" ? "" : (form.sim_id || "") };
    try {
      if (editing) await api.put(`/device-details/${editing.id}`, payload);
      else await api.post("/device-details", payload);
      toast.success(editing ? "Device updated" : "Device created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete IMEI ${row.device_imei_number}?`)) return;
    try {
      await api.delete(`/device-details/${row.id}`);
      toast.success("Deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const filtered = useMemo(() => {
    const f = filter.toLowerCase();
    return items.filter((r) => !f ||
      [r.device_imei_number, r.device_model_name, r.status].some((x) =>
        (x || "").toLowerCase().includes(f)
      )
    );
  }, [items, filter]);

  const modelOptions = models
    .filter((m) => m.status === "Active")
    .map((m) => ({
      id: m.id,
      label: [m.manufacturer, m.model].filter(Boolean).join(" "),
    }));

  return (
    <div data-testid="device-details-page">
      <PageHeader
        overline="Masters"
        title="Device Details"
        description="IMEI inventory — each row is a unique device (model + IMEI)."
        actions={
          <div className="flex gap-2">
            <BulkUploadButton
              templateUrl="/device-details/sample-template"
              uploadUrl="/device-details/bulk-upload"
              templateName="device_details_template.xlsx"
              label="Bulk Upload"
              testIdPrefix="device-bulk"
              onDone={load}
            />
            <Button
              data-testid="device-add-button"
              onClick={openNew}
              className="rounded-none vsm-primary-bg hover:opacity-90"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Device
            </Button>
          </div>
        }
      />

      <div className="px-4 sm:px-6 md:px-10 py-5 sm:py-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="relative max-w-md w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 vsm-muted" />
            <Input
              data-testid="device-quick-search"
              className="rounded-none pl-9"
              placeholder="Search by IMEI, model, status…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <div className="vsm-overline ml-auto">{filtered.length} record(s)</div>
        </div>

        <div className="border vsm-border vsm-surface overflow-x-auto vsm-table-scroll" data-testid="devices-table">
          <table className="w-full text-sm whitespace-nowrap">
            <thead>
              <tr className="border-b vsm-border text-left">
                <Th>Device Model</Th>
                <Th>IMEI Number</Th>
                <Th>SIM Provider · Account</Th>
                <Th>SIM Number</Th>
                <Th>SIM Serial Number</Th>
                <Th>Status</Th>
                <Th>Assigned</Th>
                <Th className="w-28 text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 vsm-muted text-center">
                    No devices yet.
                  </td>
                </tr>
              )}
              {filtered.map((r) => (
                <tr key={r.id} className="border-b vsm-border hover:bg-zinc-50 dark:hover:bg-zinc-800/40">
                  <Td className="font-semibold">{r.device_model_name || "—"}</Td>
                  <Td className="font-mono">{r.device_imei_number}</Td>
                  <Td>
                    {r.sim_service_provider_name || "—"}
                    {r.sim_service_provider_account_number && (
                      <span className="vsm-muted ml-1">· {r.sim_service_provider_account_number}</span>
                    )}
                  </Td>
                  <Td className="font-mono text-xs">{r.mobile_number || "—"}</Td>
                  <Td className="font-mono text-xs">{r.sim_no || "—"}</Td>
                  <Td><StatusBadge status={r.status} /></Td>
                  <Td className="text-xs">{r.assigned_to_vehicle_id ? "🚗 Vehicle" : "Available"}</Td>
                  <Td className="text-right">
                    <button
                      onClick={() => openEdit(r)}
                      data-testid={`device-edit-${r.id}`}
                      className="p-1.5 hover:bg-zinc-100 dark:hover:bg-zinc-800 mr-1"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {isAdmin && (
                      <button
                        onClick={() => remove(r)}
                        data-testid={`device-delete-${r.id}`}
                        className="p-1.5 hover:bg-red-100 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none border vsm-border max-w-lg" data-testid="device-dialog">
          <DialogHeader>
            <div className="vsm-overline">{editing ? "Edit" : "New"}</div>
            <DialogTitle className="text-2xl font-bold tracking-tight">
              {editing ? `Edit ${editing.device_imei_number}` : "New Device"}
            </DialogTitle>
            <DialogDescription className="vsm-muted text-sm">
              Pick the Device Model from the master list, then enter the unique IMEI.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4 mt-2">
            <div>
              <Label className="vsm-overline">Device Model <span className="text-red-600">*</span></Label>
              <div className="mt-2">
                <SelectWithAdd
                  testId="device-model"
                  value={form.device_model_id}
                  onChange={(v) => setForm({ ...form, device_model_id: v })}
                  options={modelOptions}
                  placeholder="Select model"
                />
              </div>
            </div>
            <div>
              <Label className="vsm-overline">Device IMEI Number <span className="text-red-600">*</span></Label>
              <Input
                data-testid="device-imei"
                className="rounded-none mt-2 font-mono"
                value={form.device_imei_number}
                onChange={(e) => setForm({ ...form, device_imei_number: e.target.value })}
              />
            </div>
            <div>
              <Label className="vsm-overline">Status</Label>
              <div className="mt-2">
                <SelectWithAdd
                  testId="device-status"
                  value={form.status}
                  onChange={(v) => setForm({ ...form, status: v })}
                  options={[{ id: "Active", label: "Active" }, { id: "Inactive", label: "Inactive" }]}
                />
              </div>
            </div>
            <div className="border-t vsm-border pt-4">
              <Label className="vsm-overline">SIM Assignment (optional)</Label>
              <p className="text-xs vsm-muted mt-1">Only SIMs not already assigned to another device are shown.</p>
              <div className="mt-2">
                <SelectWithAdd
                  testId="device-sim"
                  value={form.sim_id || "__none__"}
                  onChange={(v) => setForm({ ...form, sim_id: v === "__none__" ? "" : v })}
                  options={[
                    { id: "__none__", label: "— No SIM —" },
                    ...availSims.map((s) => ({
                      id: s.id,
                      label:
                        [
                          s.sim_service_provider_name,
                          s.sim_service_provider_account_number,
                          s.mobile_number,
                          s.sim_no,
                        ]
                          .filter(Boolean)
                          .join(" · "),
                    })),
                  ]}
                  placeholder="No SIM"
                />
              </div>
            </div>
            <DialogFooter className="pt-2">
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setOpen(false)} data-testid="device-cancel">Cancel</Button>
              <Button type="submit" disabled={saving} className="rounded-none vsm-primary-bg hover:opacity-90" data-testid="device-save">
                {saving ? "Saving…" : "Save Device"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
